/**
 * apps/command/src/components/AppShell.tsx — §15.4's frame.
 *
 * A fixed left rail with the state emblem on a gold hairline, and a top bar
 * carrying the context title, the environment badge, the operator's identity
 * and the sign-out.
 *
 * ============================================================================
 * THE RAIL SHOWS ONLY WHAT THIS ACCOUNT CAN OPEN.
 * ============================================================================
 *
 * Every entry is capability-gated, so a governor's rail is four items and an
 * operator's is six. That is §15.7 applied to navigation rather than only to
 * the landing screen: "no configuration, no dense tables, nothing that can be
 * broken by a curious click." A greyed-out item an official cannot use is an
 * invitation to ask why, and the answer is never interesting.
 *
 * The rail is collapsed to 72 px by default and expands to 240 px. It does NOT
 * remember the choice across reloads in P0 — saved views and density are §15.9
 * work for P6, and a half-built preference store would be worse than none.
 */
import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { AdminCapability, AdminRole } from '@navoiy/domain';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { Gerb } from '../ui/Gerb.jsx';
import {
  IconAlerts,
  IconAds,
  IconBroadcast,
  IconCamera,
  IconChannels,
  IconScreens,
  IconCityMap,
  IconMap,
  IconBulk,
  IconConsole,
  IconDavomat,
  IconStaff,
  IconExecutive,
  IconExternal,
  IconFleet,
  IconMahalla,
  IconQueue,
  IconRemediation,
  IconSignOut,
} from '../ui/icons.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';

/**
 * A heading in the rail rather than a destination.
 *
 * The house console groups its rail this way and it is worth copying for a
 * reason beyond matching: fourteen flat links is a list somebody scans; four
 * groups of three or four is a list somebody LEARNS, and an operator who has
 * learnt where «Kamera devori» lives stops reading the rail at all.
 */
interface RailSection {
  readonly section: string;
}

interface RailEntry {
  to: string;
  labelKey: string;
  capability: AdminCapability;
  /**
   * The rail glyph, from `src/ui/icons.tsx` (Phosphor, `regular`).
   *
   * These were two-letter uz-Latn abbreviations — `RK`, `BK`, `KI` — and the
   * comment here said why: "§15's design language has no sanctioned icon tier of
   * its own yet, and an arbitrary icon pack would be a second visual vocabulary
   * arriving by accident."
   *
   * The reasoning was right and its premise is now wrong: `apps/webapp` draws
   * the Mini App's glyphs from `@phosphor-icons/react` by owner directive, so
   * the platform HAS a sanctioned web icon set and this is it reaching its third
   * surface rather than a pack arriving by accident. See `src/ui/icons.tsx`.
   *
   * THE ICON IS NEVER THE ONLY LABEL: the expanded rail shows the word, and the
   * collapsed rail keeps it in `title`.
   */
  Icon: (props: { className?: string }) => React.JSX.Element;
  /**
   * Roles for which this entry is part of the job, when the capability alone is
   * too broad.
   *
   * Used by exactly one entry, «Mening mahallam», and the distinction is
   * §15.7's: a governor HOLDS `mbt.fleet.read` and could open a single-mahalla
   * view, but "my mahalla" is not a thing a city governor has. This is the
   * persona rule that `mbtHomeRoute` encodes for landings, applied to
   * navigation — an ergonomics decision, never an access one, which is why the
   * capability gate above it still runs and the route still checks it.
   */
  roles?: readonly AdminRole[];
}

const RAIL: readonly (RailEntry | RailSection)[] = [
  { section: 'nav.section.command' },
  { to: '/', labelKey: 'nav.home', capability: 'mbt.overview.read', Icon: IconExecutive },
  { to: '/console', labelKey: 'nav.console', capability: 'mbt.fleet.read', Icon: IconConsole },
  { to: '/fleet', labelKey: 'nav.fleet', capability: 'mbt.fleet.read', Icon: IconFleet },
  {
    to: '/channels',
    labelKey: 'nav.channels',
    capability: 'mbt.overview.read',
    Icon: IconChannels,
  },
  {
    to: '/mahalla',
    labelKey: 'nav.mahalla',
    capability: 'mbt.fleet.read',
    Icon: IconMahalla,
    roles: ['mahalla_lead'],
  },
  { section: 'nav.section.operations' },
  { to: '/queue', labelKey: 'nav.queue', capability: 'mbt.fleet.read', Icon: IconQueue },
  { to: '/alerts', labelKey: 'nav.alerts', capability: 'mbt.fleet.read', Icon: IconAlerts },
  { to: '/remediation', labelKey: 'nav.remediation', capability: 'mbt.fleet.read', Icon: IconRemediation },
  { to: '/bulk', labelKey: 'nav.bulk', capability: 'mbt.fleet.read', Icon: IconBulk },
  /**
   * The camera wall and the map sit with the fleet, not with the broadcast
   * tools: they are how an operator LOOKS at the province. The wall is behind
   * `mbt.live.view` (§5 — watching a public space), narrower than fleet read.
   */
  /**
   * The risk centre leads the watch section: it is the screen that answers
   * «what should I look at first», and the two below it are how you look.
   */
  { section: 'nav.section.watch' },
  { to: '/risk', labelKey: 'nav.risk', capability: 'mbt.fleet.read', Icon: IconAlerts },
  { to: '/camera', labelKey: 'nav.camera', capability: 'mbt.live.view', Icon: IconCamera },
  /**
   * The screen wall sits beside the camera wall and behind the SAME capability.
   * It shows less of a citizen than the camera does — a UI rather than a face —
   * but it can show what one identified person typed, so it does not get a
   * wider audience than the lobby camera has.
   */
  { to: '/screens', labelKey: 'nav.screens', capability: 'mbt.live.view', Icon: IconScreens },
  /**
   * MBT-X — the city map, ABOVE «Operativ xarita» and behind the overview
   * capability rather than fleet read.
   *
   * Two map entries, deliberately: `/map` is the FLEET on geography — which
   * kiosk is offline and where — and this one is the CITY, the same picture a
   * citizen sees at a mahalla kiosk. The owner's ruling forbids putting kiosk
   * status on it, so the two cannot merge without losing one of the two
   * questions. The city comes first because it is the one an official opens
   * without an incident to chase.
   */
  { to: '/xarita', labelKey: 'nav.xarita', capability: 'mbt.overview.read', Icon: IconCityMap },
  { to: '/map', labelKey: 'nav.map', capability: 'mbt.fleet.read', Icon: IconMap },
  { section: 'nav.section.publish' },
  { to: '/broadcast', labelKey: 'nav.broadcast', capability: 'mbt.overview.read', Icon: IconBroadcast },
  /**
   * §6.2's ads sit behind `mbt.ads.manage` — governor and admin — rather than
   * behind the overview capability every MBT role holds. An operator who can
   * see the fleet has no business seeing the commercial bookings on it.
   */
  { to: '/ads', labelKey: 'nav.ads', capability: 'mbt.ads.manage', Icon: IconAds },
  /**
   * §7's board is behind `mbt.attendance.read`, not `mbt.overview.read`.
   *
   * The overview capability is held by every MBT role including `operator` and
   * `content`; the board is people's working days, and it is held by governor,
   * hr and mahalla_lead. Leaving the nav on the wider capability would show an
   * operator a link that always refuses.
   */
  { section: 'nav.section.people' },
  { to: '/davomat', labelKey: 'nav.davomat', capability: 'mbt.attendance.read', Icon: IconDavomat },
  /**
   * The staff register, under davomat and behind `mbt.staff.manage` — hr and
   * admin only.
   *
   * A NARROWER CAPABILITY THAN THE BOARD ABOVE IT, deliberately: reading who is
   * at their desk is a mahalla lead's job, and adding a person to the citizen
   * directory or enrolling their face is not.
   */
  { to: '/staff', labelKey: 'nav.staff', capability: 'mbt.staff.manage', Icon: IconStaff },
];

function Rail(): React.JSX.Element {
  const { allows, me } = useSession();
  const isSection = (entry: RailEntry | RailSection): entry is RailSection =>
    'section' in entry;

  const visible = RAIL.filter(
    (entry) =>
      isSection(entry) ||
      (allows(entry.capability) &&
        (!entry.roles || (me !== null && entry.roles.includes(me.role)))),
  );

  /**
   * Drop a heading whose whole group was filtered away by capability.
   *
   * Without this a `content` manager sees «KUZATUV» with nothing under it — a
   * label for a section they cannot enter, which reads as a broken panel rather
   * than as a permission boundary.
   */
  const entries = visible.filter((entry, index) => {
    if (!isSection(entry)) return true;
    const next = visible[index + 1];
    return next !== undefined && !isSection(next);
  });

  return (
    /*
      ==========================================================================
      ALWAYS OPEN, AND DARK. Both by owner directive, 2026-08-18.
      ==========================================================================

      The rail used to collapse to 72 px and default to collapsed, which meant
      every operator met this panel as a column of unlabelled glyphs. There is
      no toggle any more and no state behind it: the width is fixed, so the
      content area never reflows and nobody has to re-open the navigation on
      each machine they sit down at.

      `bg-rail` is #0F2341 — dark chrome around a light page, the house
      console's own arrangement. Everything inside therefore wears WHITE-family
      ink rather than the page's `ink-*` scale, which is tuned for dark type on
      a light panel and would be unreadable here.
    */
    <nav
      className="flex w-rail-open shrink-0 flex-col border-r border-rail bg-rail"
      aria-label={t('app.short')}
    >
      {/*
        §15.4 — the state emblem at the top, on a gold hairline.

        THE REAL GERB, not the gold ring that used to stand in for it. Same
        asset the kiosk shows citizens, so the two surfaces cannot drift into
        showing different emblems (see src/ui/Gerb.tsx).

        THE INSTITUTION'S NAME IS NOT HERE, and that was measured rather than
        assumed. «NAVOIY SHAHAR HOKIMLIGI» in caps does not fit §15.4's 240 px
        expanded rail — it renders as «NAVOIY SHAHAR HOKI…», and a truncated
        state institution is worse than no institution. Widening the rail would
        be amending §15.4 to fit a label; wrapping it to two lines would push the
        gerb off its own hairline. So the lockup lives in the top bar, which is
        always visible and has the width for it, and the rail carries the emblem
        and the system's own short name.
      */}
      {/*
        ======================================================================
        THE MASTHEAD — a lockup, not a logo with a word next to it.
        ======================================================================

        It used to be the gerb and the flat string «MBT», which is a placeholder
        that got shipped: the acronym stood alone with nothing to say what it
        stands for, and on a 260 px rail there was room for the whole thing.

        A lockup is three parts in a fixed relationship, and all three are here:

          the MARK      the state emblem, on its own, at a size where the
                        crest actually resolves rather than turning to mush
          the WORDMARK  «MBT», in gold — the only gold type in the rail, which
                        is what makes it read as an identity rather than as
                        the first item of navigation
          the DESCRIPTOR «Markaziy boshqaruv tizimi», so the acronym is
                        expanded exactly once, where somebody meeting the
                        system for the first time will look for it

        WHY THE INSTITUTION IS NOT HERE: the top bar already carries «NAVOIY
        SHAHAR HOKIMLIGI» in the same eyeline, and a government emblem with the
        hokimlik's name printed twice on one screen reads as a template nobody
        finished rather than as a masthead.

        `bg-hero` is the same navy-and-gold bloom the console's hero cards use,
        so the masthead has depth instead of being a flat rectangle at the top
        of a flat column — the specific thing that made this corner look
        unfinished.

        `h-16` matches the top bar exactly, so the gold rule under this and the
        hairline under that are ONE continuous line across the whole
        application. That alignment is most of why a masthead looks deliberate.
      */}
      <div className="flex h-16 items-center gap-3 border-b border-gold/40 bg-hero px-5">
        <Gerb className="h-9 w-auto shrink-0" />
        <span className="flex min-w-0 flex-col justify-center leading-none">
          <span className="truncate text-lead font-bold tracking-wide text-gold">
            {t('app.short')}
          </span>
          {/* `nano` — a named 10px step carrying its own tracking, added to the
              config for this one job. A descriptor, deliberately quiet: if it
              competed with the wordmark there would be two things to read and
              no hierarchy between them. */}
          <span className="mt-1 truncate text-nano uppercase text-white/55">
            {t('app.name')}
          </span>
        </span>
      </div>

      <ul className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {entries.map((entry) =>
          isSection(entry) ? (
            /* `white/45`, not `ink-500`. The page's muted ink is a mid grey
               chosen against a white panel; on #0F2341 it is nearly invisible.
               Every muted tone in this rail is therefore an alpha of white,
               which is the only scale that stays legible on the navy. */
            <li
              key={entry.section}
              className="px-3 pb-1 pt-4 text-micro font-semibold uppercase tracking-wide text-white/45"
            >
              {t(entry.section)}
            </li>
          ) : (
          <li key={entry.to}>
            <NavLink
              to={entry.to}
              end={entry.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded px-3 py-2 text-label transition-colors duration-state ${
                  /* A GOLD LEFT BAR, not just a tint. A background wash alone is
                     a weak signal at a glance, and "which screen am I on" is the
                     question a rail answers most often. The bar is `border-l-2`
                     on both states so the label does not shift by two pixels
                     when it becomes active.

                     REST IS `white/70`, NOT WHITE. If every item were full
                     white the active one would have no way to stand out except
                     the bar, and the rail would read as fourteen equally
                     important destinations. */
                  isActive
                    ? 'border-l-2 border-gold bg-white/10 font-semibold text-white'
                    : 'border-l-2 border-transparent text-white/70 hover:bg-white/5 hover:text-white'
                }`
              }
              title={t(entry.labelKey)}
            >
              <entry.Icon className="h-5 w-5 shrink-0" />
              <span className="truncate">{t(entry.labelKey)}</span>
            </NavLink>
          </li>
          ),
        )}
      </ul>

    </nav>
  );
}

export function AppShell({
  title,
  bleed = false,
  children,
}: {
  title?: string;
  /** MBT-X §1 — render the route edge to edge inside the shell, with no page
   * scroll. Used by «Shahar xaritasi» and nothing else today. */
  bleed?: boolean;
  children?: ReactNode;
}): React.JSX.Element {
  const { me, logout } = useSession();

  return (
    <div className="flex h-full bg-canvas text-ink-100">
      <Rail />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-hairline bg-panel px-6 shadow-card">
          {/*
            THE FORMAL LOCKUP, in the one place wide enough for it: the
            institution above, the screen below.

            Caps as a display treatment (`uppercase`) and never by shouting in
            the dictionary — the same rule the kiosk's attract lockup follows, so
            the string stays an ordinary sentence for every other reader of it.
            `min-w-0` so a long screen title truncates instead of pushing the
            operator's identity off the right edge.
          */}
          <div className="min-w-0">
            <p className="truncate text-micro font-semibold uppercase tracking-wide text-ink-500">
              {t('app.institution')}
            </p>
            <h1 className="truncate text-lead font-medium">{title ?? t('app.name')}</h1>
          </div>
          {/*
            ==================================================================
            THREE GROUPS, SEPARATED — not four things in a row.
            ==================================================================

            This was a flat line of same-weight grey text: a theme link, an
            external link, the operator's name, and sign-out, all reading as one
            undifferentiated strip in which the person's own identity was the
            third item. They are three different KINDS of thing and now look it:

              tools     the theme switch and the murojaat panel, as icon
                        buttons with a hit area rather than bare text
              identity  WHO IS SIGNED IN, as a chip with an initial disc and
                        the role underneath — on a console where capability
                        decides what half these screens will even let you do,
                        «which account is this» is worth more than a name in
                        grey
              exit      sign-out, alone past a divider, because it is the one
                        control here that ends something

            The dividers are what make it three groups instead of eight items.
          */}
          <div className="flex items-center gap-3">
            {/*
              §15.4's environment badge. Shown ONLY when this is not production,
              because the point is to stop somebody broadcasting to a staging
              fleet believing it is the city — a badge that is always on screen
              is a badge nobody reads.
            */}
            {import.meta.env.DEV ? (
              <span className="rounded border border-warn/40 px-2 py-0.5 text-micro font-medium text-warn">
                {t('app.envDev')}
              </span>
            ) : null}

            <div className="flex items-center gap-1">
              <ThemeToggle />
              {/* An icon BUTTON with a hit area, not a text link. It leaves this
                  application, so it gets the affordance of a control rather
                  than the weight of a sentence. */}
              <a
                /*
                  ABSOLUTE, NOT `/`. A bare `/` was correct only while this
                  panel lived at the murojaat panel's own root
                  (navoiy-admin.1finance.uz/) — Caddy's catch-all served the
                  murojaat panel there, so "/" happened to BE it. Once
                  markaz.city-portal.uz became this panel's own address, "/"
                  on that host resolves to markaz.city-portal.uz itself
                  (redirected straight back into /markaziy), so the button
                  that is supposed to LEAVE this panel just reopened it.
                  The murojaat panel's real, permanent address is the only
                  target that is correct from every host this app is served
                  from.
                */
                href="https://admin.city-portal.uz/"
                title={t('app.murojaatPanel')}
                className="flex h-9 items-center gap-2 rounded px-2.5 text-micro text-ink-500 transition-colors duration-state hover:bg-panel-raised hover:text-ink-300"
              >
                <IconExternal className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">{t('app.murojaatPanel')}</span>
              </a>
            </div>

            <span className="h-6 w-px bg-hairline" aria-hidden="true" />

            {me ? (
              <div className="flex items-center gap-2.5">
                {/*
                  THE INITIAL DISC. Not a photograph — this panel has no staff
                  avatars and inventing one would mean a second identity system.
                  One letter in the institution's navy is enough to make the
                  chip an object rather than a run of text, which is the whole
                  job it has to do.
                */}
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-label font-semibold text-white">
                  {me.full_name.trim().slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden min-w-0 flex-col leading-tight sm:flex">
                  <span className="truncate text-micro font-semibold text-ink-100">
                    {me.full_name}
                  </span>
                  {/* THE ROLE, which was nowhere on this screen. Capability
                      decides what half these pages will let you do, so «signed
                      in as an operator» explains a refused button before
                      anybody has to press it. */}
                  <span className="truncate text-nano uppercase tracking-wide text-ink-500">
                    {hasKey(`role.${me.role}`) ? t(`role.${me.role}`) : me.role}
                  </span>
                </span>
              </div>
            ) : null}

            <span className="h-6 w-px bg-hairline" aria-hidden="true" />

            <button
              type="button"
              onClick={() => void logout()}
              title={t('app.signOut')}
              className="flex h-9 items-center gap-2 rounded px-2.5 text-micro text-ink-500 transition-colors duration-state hover:bg-crit/10 hover:text-crit"
            >
              <IconSignOut className="h-4 w-4 shrink-0" />
              <span className="hidden lg:inline">{t('app.signOut')}</span>
            </button>
          </div>
        </header>

        {/*
          MBT-X §1 — ONE PROP, NO FORK.

          `bleed` bypasses the content column for the one class of object §15.4
          already exempts from it ("tables full-bleed"): a map. The rail and the
          top bar stay, because §15.9's «`Esc` retreats one level» and the
          rail's learned geography both die under the camera wall's `fixed
          inset-0` — that idiom is a modal enlargement of one frame, and a
          destination that hides its own navigation is the failure this shell
          exists to avoid.

          `overflow-hidden`, never `overflow-y-auto`: the page must not scroll
          behind a map. That is the desktop analogue of kiosk invariant 1, and
          it is checked by the screenshot gate (`scrollHeight === innerHeight`).
        */}
        <main
          className={
            bleed ? 'relative min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-y-auto'
          }
        >
          {bleed ? (
            (children ?? <Outlet />)
          ) : (
            <div className="mx-auto max-w-content p-6">{children ?? <Outlet />}</div>
          )}
        </main>
      </div>
    </div>
  );
}
